import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, MapPin, X, Filter } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDateRange } from "@/contexts/DateRangeContext";
import { Card, CardContent } from "@/components/ui/card";

// Available locations - could be fetched from API in the future
const AVAILABLE_LOCATIONS = ["501", "502", "503", "504", "506", "507", "513"];

export function GlobalFilterBar({ className }: { className?: string }) {
  const {
    dateRange,
    setDateRange,
    setCustomDateRange,
    selectedLocations,
    toggleLocation,
    clearFilters,
    presetRanges,
  } = useDateRange();

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [customRange, setCustomRange] = useState<DateRange | undefined>({
    from: dateRange.from,
    to: dateRange.to,
  });

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      setCustomRange(range);
      setCustomDateRange(range.from, range.to);
      setDatePickerOpen(false);
    } else if (range?.from) {
      setCustomRange(range);
    }
  };

  const handlePresetChange = (label: string) => {
    const preset = presetRanges.find((r) => r.label === label);
    if (preset) {
      setDateRange(preset);
      setCustomRange({ from: preset.from, to: preset.to });
    }
  };

  const hasActiveFilters = selectedLocations.length > 0;

  return (
    <Card className={cn("mb-6", className)}>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Date Range Selector */}
          <div className="flex-1 min-w-[280px]">
            <label className="text-sm font-medium mb-2 block">Date Range</label>
            <div className="flex gap-2">
              <Select
                value={dateRange.label}
                onValueChange={handlePresetChange}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presetRanges.map((range) => (
                    <SelectItem key={range.label} value={range.label}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !customRange && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Custom
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={customRange?.from}
                    selected={customRange}
                    onSelect={handleDateRangeSelect}
                    numberOfMonths={2}
                  />
                  {customRange?.from && customRange?.to && (
                    <div className="p-3 border-t">
                      <p className="text-sm text-center">
                        {format(customRange.from, "MMM dd, yyyy")} -{" "}
                        {format(customRange.to, "MMM dd, yyyy")}
                      </p>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(dateRange.from, "MMM dd, yyyy")} -{" "}
              {format(dateRange.to, "MMM dd, yyyy")}
            </p>
          </div>

          {/* Location Filter */}
          <div className="flex-1 min-w-[280px]">
            <label className="text-sm font-medium mb-2 block">Zones</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {selectedLocations.length === 0
                        ? "All Zones"
                        : selectedLocations.length === 1
                          ? `Zone ${selectedLocations[0]}`
                          : `${selectedLocations.length} Zones`}
                    </span>
                  </div>
                  <Filter className="h-4 w-4 ml-2 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="start">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm mb-3">Select Zones</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_LOCATIONS.map((location) => (
                      <button
                        key={location}
                        onClick={() => toggleLocation(location)}
                        className={cn(
                          "px-3 py-2 text-sm rounded-md border transition-colors",
                          selectedLocations.includes(location)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "hover:bg-accent",
                        )}
                      >
                        Zone {location}
                      </button>
                    ))}
                  </div>
                  {selectedLocations.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => selectedLocations.forEach(toggleLocation)}
                      className="w-full mt-2"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {selectedLocations.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedLocations.map((location) => (
                  <Badge key={location} variant="secondary" className="text-xs">
                    Zone {location}
                    <button
                      onClick={() => toggleLocation(location)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
